import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "./CardSliderWORK.css";

const asText = (value, fallback = "") => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return fallback;
};

const ScrollLink = ({ to, children, ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...props}>
      {children}
    </RouterLink>
  );
};

const CardSlider = ({
  data,
  id = "service-offerings",
  eyebrow = "Our capabilities",
  heading = "What services do we offer?",
  introduction = "Focused digital services designed to solve operational challenges, strengthen products, and support long-term growth.",
}) => {
  const items = useMemo(
    () => (Array.isArray(data) ? data.filter(Boolean) : []),
    [data],
  );
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setCurrentIndex((index) => {
      if (items.length === 0) return 0;
      return Math.min(index, items.length - 1);
    });
  }, [items.length]);

  const hasNavigation = items.length > 1;

  const goToCard = (index) => {
    if (index >= 0 && index < items.length) {
      setCurrentIndex(index);
    }
  };

  const goToPrevCard = () => {
    if (!hasNavigation) return;
    setCurrentIndex((index) => (index - 1 + items.length) % items.length);
  };

  const goToNextCard = () => {
    if (!hasNavigation) return;
    setCurrentIndex((index) => (index + 1) % items.length);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      goToPrevCard();
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      goToNextCard();
    }
  };

  const getCircularIndex = (offset) =>
    (currentIndex + offset + items.length) % items.length;

  const visibleOffsets =
    items.length <= 1 ? [0] : items.length === 2 ? [0, 1] : [-1, 0, 1];

  return (
    <section
      id={id}
      className="work-slider"
      aria-labelledby={`${id}-title`}
      onKeyDown={handleKeyDown}
    >
      <div className="work-slider__decoration" aria-hidden="true" />

      <div className="work-slider__container">
        <header className="work-slider__header">
          <div>
            <p className="work-slider__eyebrow">
              <span aria-hidden="true" />
              {eyebrow}
            </p>
            <h2 id={`${id}-title`}>{heading}</h2>
          </div>
          <p className="work-slider__introduction">{introduction}</p>
        </header>

        {items.length > 0 ? (
          <>
            <div
              className="work-slider__stage"
              tabIndex={hasNavigation ? 0 : undefined}
              aria-label="Service carousel. Use the left and right arrow keys to navigate."
            >
              <button
                type="button"
                className="work-slider__control work-slider__control--previous"
                onClick={goToPrevCard}
                disabled={!hasNavigation}
                aria-label="Show previous service"
              >
                <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
              </button>

              <div
                className={`work-slider__cards work-slider__cards--${visibleOffsets.length}`}
                aria-live="polite"
              >
                {visibleOffsets.map((offset) => {
                  const itemIndex = getCircularIndex(offset);
                  const item = items[itemIndex];
                  const isCurrent = offset === 0;
                  const title = asText(item.title ?? item.name, "Axiomos service");
                  const description = asText(
                    item.descriptionsmall ??
                      item.shortDescription ??
                      item.description,
                    "A focused digital service designed around your business goals.",
                  );
                  const category = asText(
                    item.category ?? item.type ?? item.field,
                    "Digital service",
                  );

                  return (
                    <article
                      key={`${item.id ?? itemIndex}-${currentIndex}-${offset}`}
                      className={`work-slider__card work-slider__card--${
                        isCurrent ? "current" : offset < 0 ? "previous" : "next"
                      }`}
                      aria-current={isCurrent ? "true" : undefined}
                    >
                      <div className="work-slider__card-top">
                        <span>{category}</span>
                        <small aria-hidden="true">
                          {String(itemIndex + 1).padStart(2, "0")}
                        </small>
                      </div>

                      <div className="work-slider__card-content">
                        <h3>{title}</h3>
                        <p>{description}</p>
                      </div>

                      {item.id !== undefined && item.id !== null && (
                        <ScrollLink
                          className="work-slider__card-link"
                          to={`/services/${item.id}`}
                          aria-label={`Explore ${title}`}
                        >
                          Explore service
                          <span aria-hidden="true">
                            <FontAwesomeIcon icon={faArrowRight} />
                          </span>
                        </ScrollLink>
                      )}
                    </article>
                  );
                })}
              </div>

              <button
                type="button"
                className="work-slider__control work-slider__control--next"
                onClick={goToNextCard}
                disabled={!hasNavigation}
                aria-label="Show next service"
              >
                <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
              </button>
            </div>

            <div className="work-slider__navigation">
              <p className="work-slider__counter" aria-live="polite">
                <strong>{String(currentIndex + 1).padStart(2, "0")}</strong>
                <span aria-hidden="true" />
                {String(items.length).padStart(2, "0")}
              </p>

              {hasNavigation && (
                <div className="work-slider__dots" aria-label="Choose a service">
                  {items.map((item, index) => (
                    <button
                      type="button"
                      key={item.id ?? item.title ?? index}
                      className={index === currentIndex ? "is-active" : ""}
                      onClick={() => goToCard(index)}
                      aria-label={`Show service ${index + 1}`}
                      aria-current={index === currentIndex ? "true" : undefined}
                    >
                      <span />
                    </button>
                  ))}
                </div>
              )}

              <p className="work-slider__keyboard-hint">Use arrow keys to explore</p>
            </div>
          </>
        ) : (
          <div className="work-slider__empty" role="status">
            <p>Service information will be available soon.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CardSlider;
